import React, { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import SEO from './SEO';
import { blogPosts, getAllCategories, getAllTags } from '../data/blog/index';
import '../styles/Blog.css';

const BlogPage = () => {
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedTag, setSelectedTag] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');

  // Scroll al inicio cuando se carga la página
  React.useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const categories = getAllCategories();
  const tags = getAllTags();

  // Filtrar posts según criterios
  const filteredPosts = useMemo(() => {
    return blogPosts.filter(post => {
      const matchesCategory = selectedCategory === 'all' || post.category === selectedCategory;
      const matchesTag = selectedTag === 'all' || post.tags.includes(selectedTag);
      const matchesSearch = searchTerm === '' || 
        post.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        post.description.toLowerCase().includes(searchTerm.toLowerCase());
      
      return matchesCategory && matchesTag && matchesSearch;
    });
  }, [selectedCategory, selectedTag, searchTerm]);

  const seoData = {
    title: 'Blog',
    description: 'Artículos, guías y recursos sobre automatización de atención al cliente, chatbots con IA, estrategias de marketing digital y tecnología para empresas.',
    keywords: 'blog runamatic, automatización, chatbots, inteligencia artificial, whatsapp business, marketing digital, atención al cliente',
    canonical: '/blog',
    schema: {
      "@context": "https://schema.org",
      "@type": "Blog",
      "name": "Blog Runamatic",
      "description": "Artículos sobre automatización, IA y tecnología para empresas",
      "url": "https://runamatic.io/blog",
      "publisher": {
        "@type": "Organization",
        "name": "Runamatic",
        "logo": {
          "@type": "ImageObject",
          "url": "https://runamatic.io/LogoNegro.png"
        }
      }
    }
  };

  return (
    <>
      <SEO {...seoData} />
      <div className="blog-page">
        {/* Hero Section */}
        <section className="blog-hero">
          <div className="blog-hero-background">
            <div className="blog-hero-shape blog-hero-shape-1"></div>
            <div className="blog-hero-shape blog-hero-shape-2"></div>
            <div className="blog-hero-shape blog-hero-shape-3"></div>
          </div>
          
          <div className="blog-hero-content">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="blog-hero-title">Blog de Runamatic</h1>
              <p className="blog-hero-subtitle">
                Descubre estrategias, tendencias y mejores prácticas para automatizar y optimizar la atención a tus clientes
              </p>
            </motion.div>

            {/* Barra de búsqueda */}
            <motion.div
              className="blog-search-container"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="blog-search-wrapper">
                <i className="fas fa-search"></i>
                <input
                  type="text"
                  placeholder="Buscar artículos..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="blog-search-input"
                />
                {searchTerm && (
                  <button 
                    className="blog-search-clear"
                    onClick={() => setSearchTerm('')}
                    aria-label="Limpiar búsqueda"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>
            </motion.div>
          </div>
        </section>

        {/* Filters Section */}
        <section className="blog-filters">
          <div className="blog-container">
            <motion.div
              className="filters-wrapper"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.3 }}
            >
              {/* Filtro por categoría */}
              <div className="filter-group">
                <label className="filter-label">
                  <i className="fas fa-folder"></i>
                  Categoría
                </label>
                <div className="filter-buttons">
                  <button
                    className={`filter-btn ${selectedCategory === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedCategory('all')}
                  >
                    Todas
                  </button>
                  {categories.map(category => (
                    <button
                      key={category}
                      className={`filter-btn ${selectedCategory === category ? 'active' : ''}`}
                      onClick={() => setSelectedCategory(category)}
                    >
                      {category}
                    </button>
                  ))}
                </div>
              </div>

              {/* Filtro por tags */}
              <div className="filter-group">
                <label className="filter-label">
                  <i className="fas fa-tags"></i>
                  Etiquetas
                </label>
                <div className="filter-tags">
                  <button
                    className={`tag-btn ${selectedTag === 'all' ? 'active' : ''}`}
                    onClick={() => setSelectedTag('all')}
                  >
                    Todas
                  </button>
                  {tags.map(tag => (
                    <button
                      key={tag}
                      className={`tag-btn ${selectedTag === tag ? 'active' : ''}`}
                      onClick={() => setSelectedTag(tag)}
                    >
                      #{tag}
                    </button>
                  ))}
                </div>
              </div>
            </motion.div>

            {/* Contador de resultados */}
            <motion.div
              className="blog-results-count"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.4, delay: 0.4 }}
            >
              {filteredPosts.length === 0 ? (
                <p>No se encontraron artículos con estos filtros</p>
              ) : (
                <p>
                  {filteredPosts.length} {filteredPosts.length === 1 ? 'artículo' : 'artículos'}
                  {(selectedCategory !== 'all' || selectedTag !== 'all' || searchTerm) && ' encontrados'}
                </p>
              )}
            </motion.div>
          </div>
        </section>

        {/* Posts Grid */}
        <section className="blog-posts-section">
          <div className="blog-container">
            {filteredPosts.length === 0 ? (
              <motion.div
                className="blog-no-results"
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
              >
                <i className="fas fa-search"></i>
                <h3>No encontramos artículos</h3>
                <p>Intenta con otros filtros o términos de búsqueda</p>
                <button
                  className="blog-reset-btn"
                  onClick={() => {
                    setSelectedCategory('all');
                    setSelectedTag('all');
                    setSearchTerm('');
                  }}
                >
                  Limpiar filtros
                </button>
              </motion.div>
            ) : (
              <div className="blog-posts-grid">
                {filteredPosts.map((post, index) => (
                  <motion.article
                    key={post.id}
                    className={`blog-post-card ${post.featured ? 'featured' : ''}`}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    whileHover={{ y: -8 }}
                  >
                    <Link to={`/blog/${post.slug}`} className="blog-post-link">
                      <div className="blog-post-image">
                        <h3 className="blog-post-image-title">{post.title}</h3>
                      </div>

                      <div className="blog-post-content">
                        <div className="blog-post-meta">
                          <span className="blog-post-category">
                            <i className="fas fa-folder"></i>
                            {post.category}
                          </span>
                          <span className="blog-post-date">
                            <i className="far fa-calendar"></i>
                            {new Date(post.date).toLocaleDateString('es-ES', {
                              year: 'numeric',
                              month: 'long',
                              day: 'numeric'
                            })}
                          </span>
                        </div>

                        <p className="blog-post-description">{post.description}</p>

                        <div className="blog-post-footer">
                          <div className="blog-post-author">
                            <i className="fas fa-user-circle"></i>
                            <span>{post.author}</span>
                          </div>
                          <div className="blog-post-read-time">
                            <i className="far fa-clock"></i>
                            <span>{post.readTime}</span>
                          </div>
                        </div>

                        <div className="blog-post-tags">
                          {post.tags.slice(0, 3).map(tag => (
                            <span key={tag} className="blog-post-tag">
                              #{tag}
                            </span>
                          ))}
                        </div>
                      </div>
                    </Link>
                  </motion.article>
                ))}
              </div>
            )}
          </div>
        </section>

        {/* CTA Section */}
        <section className="blog-cta-section">
          <div className="blog-container">
            <motion.div
              className="blog-cta-content"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <h2>¿Listo para automatizar tu atención al cliente?</h2>
              <p>Descubre cómo Runamatic puede transformar la forma en que te comunicas con tus clientes</p>
              <div className="blog-cta-buttons">
                <Link to="/#demo" className="blog-cta-btn primary">
                  Solicitar demo
                  <i className="fas fa-arrow-right"></i>
                </Link>
                <Link to="/#plans" className="blog-cta-btn secondary">
                  Ver planes
                </Link>
              </div>
            </motion.div>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogPage;
